import React, { useState, useEffect } from 'react';
import { FiCopy, FiKey, FiCheckCircle, FiPlus, FiTrash2, FiEye, FiEyeOff } from 'react-icons/fi';
import IconWrapper from '../../IconWrapper';
import { Timestamp } from '../../ui/timestamp';
import { useAuth } from '../../../contexts/AuthContext';
import { config } from '../../../config';
import DeveloperGuideTab from './api-keys/DeveloperGuideTab';

interface APIKey {
  api_key_id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  last_used: string | null;
  created_at: string;
}

interface NewAPIKeyResponse {
  api_key_id: string;
  name: string;
  api_key: string;
  created_at: string;
}

const APIKeysView: React.FC = () => {
  const { user, token } = useAuth();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('api-keys');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [createKeyModalOpen, setCreateKeyModalOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState<NewAPIKeyResponse | null>(null);
  const [showNewKey, setShowNewKey] = useState(true);

  // Fetch API keys
  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${config.API_URL}/v1/auth/api-keys`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const keys = await response.json();
        setApiKeys(keys);
      } else {
        console.error('Failed to fetch API keys');
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [token]);

  // Copy to clipboard
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  // Create new API key
  const createApiKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newKeyName.trim() }),
      });

      if (response.ok) {
        const newKeyData = await response.json();
        setNewKey(newKeyData);
        setNewKeyName('');
        fetchApiKeys(); // Refresh the list
      } else {
        console.error('Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  // Delete API key
  const deleteApiKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchApiKeys(); // Refresh the list
      } else {
        console.error('Failed to delete API key');
      }
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  const tabs = [
    ...(user?.role === 'admin' ? [{ id: 'developers', label: 'Developers' }] : []),
    { id: 'api-keys', label: 'API Keys' },
  ];

  // Set default tab based on user role
  useEffect(() => {
    if (user?.role !== 'admin' && activeTab === 'developers') {
      setActiveTab('api-keys');
    }
  }, [user?.role, activeTab]);

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Header with tabs */}
      {tabs.length > 1 && (
        <div className="bg-white border-b border-gray-200">
          <div className="px-8">
            <nav className="flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {activeTab === 'developers' && (
          <DeveloperGuideTab copySuccess={copySuccess} onCopy={copyToClipboard} />
        )}

        {activeTab === 'api-keys' && (
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* API Keys Management */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <IconWrapper icon={FiKey} className="mr-2" />
                    API Keys
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Manage API keys for programmatic access to the Good Outcomes API
                  </p>
                </div>
                <button
                  onClick={() => setCreateKeyModalOpen(true)}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center"
                >
                  <IconWrapper icon={FiPlus} className="mr-2" />
                  Create API Key
                </button>
              </div>

              {/* API Keys List */}
              {apiKeys.length === 0 ? (
                <div className="text-center py-12">
                  <IconWrapper icon={FiKey} className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating a new API key.</p>
                  <div className="mt-6">
                    <button
                      onClick={() => setCreateKeyModalOpen(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
                    >
                      <IconWrapper icon={FiPlus} className="-ml-1 mr-2 h-5 w-5" />
                      Create API Key
                    </button>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Key
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Used
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="relative px-6 py-3">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {apiKeys.map((key) => (
                        <tr key={key.api_key_id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {key.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <code className="bg-gray-100 px-2 py-1 rounded">
                              {key.key_prefix}...
                            </code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              key.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {key.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <Timestamp value={key.last_used} format="absolute" fallback="Never" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <Timestamp value={key.created_at} format="absolute" />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => deleteApiKey(key.api_key_id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <IconWrapper icon={FiTrash2} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Key Modal */}
      {createKeyModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setCreateKeyModalOpen(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Create New API Key
                </h3>
                <div>
                  <label htmlFor="key-name" className="block text-sm font-medium text-gray-700">
                    Key Name
                  </label>
                  <input
                    type="text"
                    id="key-name"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="e.g., Production API Key"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => {
                    createApiKey();
                    setCreateKeyModalOpen(false);
                  }}
                  disabled={!newKeyName.trim()}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setCreateKeyModalOpen(false);
                    setNewKeyName('');
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Key Display Modal */}
      {newKey && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <IconWrapper icon={FiKey} className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      API Key Created Successfully
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 mb-4">
                        Make sure to copy your API key now. You won't be able to see it again!
                      </p>
                      <div className="bg-gray-50 rounded-md p-4">
                        <div className="flex items-center justify-between">
                          <code className="text-sm text-gray-800 break-all pr-2">
                            {showNewKey ? newKey.api_key : '•'.repeat(40)}
                          </code>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setShowNewKey(!showNewKey)}
                              className="p-1 text-gray-500 hover:text-gray-700"
                            >
                              <IconWrapper icon={showNewKey ? FiEyeOff : FiEye} className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => copyToClipboard(newKey.api_key, 'new-key')}
                              className="p-1 text-gray-500 hover:text-gray-700"
                            >
                              {copySuccess === 'new-key' ? (
                                <IconWrapper icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                              ) : (
                                <IconWrapper icon={FiCopy} className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setNewKey(null)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysView;